import { useState } from "react";
import {
  Button,
  Card,
  Flex,
  Grid,
  Label,
  TextAreaField,
  TextField,
} from "@aws-amplify/ui-react";
import LocalizedStrings from "react-localization";
import { AddCircleOutline } from "@mui/icons-material";
import * as Icons from "@mui/icons-material";
import { HexColorPicker } from "react-colorful";

import LabelRequired from "components/utils/LabelRequired";
import IconList from "components/IconList";
import CustomPopover from "components/utils/CustomPopover";

const Square = Icons.Square;

const ActionValueForm = ({ title, values, onChange }) => {
  const [iconState, setIconState] = useState({
    visible: false,
    selected: "",
    index: null,
  });

  const addInput = () => {
    onChange([
      ...values,
      {
        name: "",
        value: "",
        successMsg: "",
        errorMsg: "",
        icon: "",
        iconColor: "",
      },
    ]);
  };

  const removeInput = (index) => {
    const listUpdated = [...values];
    listUpdated.splice(index, 1);
    onChange(listUpdated);
  };

  const onChangeInput = (index, name, value) => {
    const listUpdated = [...values];
    listUpdated[index] = {
      ...listUpdated[index],
      [name]: value,
    };
    onChange(listUpdated);
  };

  const onSelectIcon = (icon) => {
    onChangeInput(iconState.index, "icon", icon);
    setIconState(() => ({
      visible: false,
      selected: "",
      index: null,
    }));
  };

  return (
    <>
      <Flex alignItems="center">
        <div className="fs-5">{title}</div>
        <div style={styles.iconAction} onClick={addInput}>
          <AddCircleOutline color="action" />
        </div>
      </Flex>

      {values?.length > 0 && (
        <Grid rowGap="15px" templateColumns={{ base: "1fr" }}>
          {values?.map((item, index) => {
            const IconComponent = Icons[item.icon || "Bolt"];

            return (
              <Card
                height="fit-content"
                borderRadius="medium"
                variation="outlined"
              >
                <Grid columnGap="15px" rowGap="15px">
                  <Grid
                    columnGap="15px"
                    rowGap="15px"
                    templateColumns={{ base: "1fr", large: "2fr 2fr" }}
                  >
                    <TextField
                      name="name"
                      label={<LabelRequired text={strings.fields.name.label} />}
                      value={item.name}
                      onChange={(event) =>
                        onChangeInput(index, "name", event.target.value)
                      }
                    ></TextField>
                    <TextField
                      name="value"
                      label={
                        <LabelRequired text={strings.fields.value.label} />
                      }
                      value={item.value}
                      onChange={(event) =>
                        onChangeInput(index, "value", event.target.value)
                      }
                    ></TextField>
                  </Grid>
                  <TextAreaField
                    name="errorMsg"
                    label={strings.fields.errorMsg.label}
                    value={item.errorMsg}
                    onChange={(event) =>
                      onChangeInput(index, "errorMsg", event.target.value)
                    }
                  ></TextAreaField>
                  <TextAreaField
                    name="successMsg"
                    label={strings.fields.successMsg.label}
                    value={item.successMsg}
                    onChange={(event) =>
                      onChangeInput(index, "successMsg", event.target.value)
                    }
                  ></TextAreaField>
                  <Flex>
                    <Grid>
                      <Label>{strings.fields.icon.label}</Label>
                      <Button
                        title={item.icon || "Bolt"}
                        variation="link"
                        colorTheme="overlay"
                        borderColor="gray"
                        size="small"
                        width="100px"
                        style={{ marginTop: "8px" }}
                        onClick={() =>
                          setIconState({
                            visible: true,
                            selected: item.icon || "Bolt",
                            index,
                          })
                        }
                      >
                        <Grid
                          className="px-1"
                          style={{ justifyItems: "center" }}
                        >
                          <IconComponent style={{ fontSize: "30px" }} />
                          <div style={styles.iconName}>
                            {item.icon || "Bolt"}
                          </div>
                        </Grid>
                      </Button>
                    </Grid>
                    <TextField
                      name="iconColor"
                      label={strings.fields.iconColor.label}
                      value={item.iconColor}
                      onChange={(event) =>
                        onChangeInput(index, "iconColor", event.target.value)
                      }
                      outerEndComponent={
                        <CustomPopover
                          content={
                            <HexColorPicker
                              style={{ margin: "20px" }}
                              color={item.iconColor ?? ""}
                              onChange={(color) =>
                                onChangeInput(index, "iconColor", color)
                              }
                            />
                          }
                        >
                          <Button color={item.iconColor ?? ""}>
                            <Square />
                          </Button>
                        </CustomPopover>
                      }
                    ></TextField>
                  </Flex>
                </Grid>
                <Button
                  size="small"
                  style={styles.buttonDelete}
                  children={strings.fields.buttons.delete}
                  colorTheme="error"
                  onClick={() => removeInput(index)}
                />
              </Card>
            );
          })}
        </Grid>
      )}
      <IconList
        visible={iconState.visible}
        selected={iconState.selected}
        onSelect={onSelectIcon}
        onClose={() =>
          setIconState(() => ({
            visible: false,
            selected: "",
          }))
        }
      />
    </>
  );
};

const styles = {
  iconAction: {
    marginLeft: "-5px",
    marginTop: "5px",
    cursor: "pointer",
  },
  buttonDelete: {
    float: "right",
    marginTop: "15px",
  },
  iconName: {
    textOverflow: "ellipsis",
    width: "90px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textAlign: "center",
  },
};

const strings = new LocalizedStrings({
  es: {
    fields: {
      name: {
        label: "Nombre",
        placeholder: "Ingrese nombre",
      },
      value: {
        label: "Valor",
        placeholder: "Ingrese valor",
      },
      errorMsg: {
        label: "Mensaje de error",
        placeholder: "Ingrese mensaje de error",
      },
      successMsg: {
        label: "Mensaje de éxito",
        placeholder: "Ingrese mensaje de éxito",
      },
      icon: {
        label: "Icono",
      },
      iconColor: {
        label: "Color de Icono",
      },
      buttons: {
        delete: "Eliminar",
      },
    },
  },
});

export default ActionValueForm;
